import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { BsChevronDown } from 'react-icons/bs';

function normalizeSearchToken(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Grouped search + optional free text, aligned with manager RentalFormModal / GroupedSearchSelect.
 * Values: `hotel:id`, `place:id`, or plain string for custom location (pricing uses region only when ref resolves).
 */
export function displayForRef(raw, hotels, places) {
  if (raw == null || String(raw).trim() === '') return '';
  const s = String(raw);
  const m = s.match(/^(hotel|place):(\d+)$/i);
  if (!m) return s;
  const idNum = Number(m[2]);
  if (m[1].toLowerCase() === 'hotel') {
    const h = hotels.find((x) => Number(x.id) === idNum);
    return h ? [h.name, h.destination_name].filter(Boolean).join(' — ') : s;
  }
  const p = places.find((x) => Number(x.id) === idNum);
  return p ? [p.name, p.destination_name].filter(Boolean).join(' — ') : s;
}

/** Resolved pick-up ref type for icons and styling (`hotel:123`, `place:456`, or other). */
export function carRentalLocationRefKind(raw) {
  if (raw == null || String(raw).trim() === '') return 'custom';
  const m = String(raw).trim().match(/^(hotel|place):(\d+)$/i);
  if (!m) return 'custom';
  return m[1].toLowerCase() === 'hotel' ? 'hotel' : 'place';
}

export default function CarLocationSearchSelect({
  value,
  onChange,
  className = '',
  bare = false,
  /** When `value` is empty, show this ref’s label (e.g. drop-off mirrors pick-up until user picks another). */
  fallbackValue = '',
  /** Primary border on trigger: parent sets when values are synced vs only drop-off changed. */
  valueBorderActive = false,
  /** Validation: red border (e.g. submit without required location). */
  isInvalid = false,
  hotels = [],
  places = [],
  loading = false,
  placeholder = 'Select hotel or other place',
  searchPlaceholder = 'Search hotels, places',
  disabled = false,
  id,
  'aria-label': ariaLabel,
  /** e.g. id of validation message when field is invalid */
  'aria-describedby': ariaDescribedBy,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);

  const groups = useMemo(() => {
    const hOpts = hotels.map((h) => ({
      value: `hotel:${h.id}`,
      label: h.name,
      secondary: h.destination_name || h.region_name || null,
    }));
    const pOpts = places.map((p) => ({
      value: `place:${p.id}`,
      label: p.name,
      secondary: p.destination_name || null,
    }));
    const out = [];
    if (hOpts.length) out.push({ label: 'Hotels', options: hOpts });
    if (pOpts.length) out.push({ label: 'Places', options: pOpts });
    return out;
  }, [hotels, places]);

  const selectedDisplay = useMemo(() => {
    const v = value == null ? '' : String(value).trim();
    if (v) return displayForRef(v, hotels, places);
    const fb = fallbackValue == null ? '' : String(fallbackValue).trim();
    if (fb) return displayForRef(fb, hotels, places);
    return '';
  }, [value, fallbackValue, hotels, places]);

  const filtered = useMemo(() => {
    const t = normalizeSearchToken(q);
    if (!t) return groups;
    return groups
      .map((g) => ({
        ...g,
        options: g.options.filter(
          (o) =>
            normalizeSearchToken(o.label).includes(t) ||
            normalizeSearchToken(o.secondary || '').includes(t),
        ),
      }))
      .filter((g) => g.options.length > 0);
  }, [groups, q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      const inputEl = searchInputRef.current;
      if (inputEl && typeof inputEl.setSelectionRange === 'function') {
        const len = String(inputEl.value || '').length;
        inputEl.setSelectionRange(len, len);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQ('');
  };

  const pickCustom = () => {
    const t = q.trim();
    if (!t) return;
    onChange(t);
    setOpen(false);
    setQ('');
  };

  const toggleOpen = () => {
    if (disabled) return;
    if (open) {
      setOpen(false);
      setQ('');
      return;
    }
    setOpen(true);
  };

  return (
    <div
      className={`position-relative car-rental-location-search${bare ? ' car-rental-location-search--bare' : ''}${valueBorderActive ? ' car-rental-location-search--value-active' : ''}${isInvalid ? ' car-rental-location-search--invalid' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
      data-allow-browser-autocomplete="true"
    >
      <button
        type="button"
        id={id}
        className={`form-control car-rental-location-search__trigger text-start d-flex align-items-center justify-content-between gap-2${isInvalid ? ' is-invalid' : ''}`}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (disabled) return;
          const key = e.key || '';
          const isPrintable = key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
          if (isPrintable) {
            e.preventDefault();
            setOpen(true);
            setQ((prev) => `${prev}${key}`);
            return;
          }
          if (key === 'Backspace') {
            e.preventDefault();
            setOpen(true);
            setQ((prev) => prev.slice(0, -1));
          }
        }}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy || undefined}
        aria-invalid={isInvalid}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-truncate min-w-0">
          {loading ? (
            selectedDisplay ? (
              <span className="text-body">{selectedDisplay}</span>
            ) : (
              <span className="text-muted d-inline-flex align-items-center gap-2">
                <Spinner animation="border" size="sm" />
                Loading locations
              </span>
            )
          ) : (
            <span className={selectedDisplay ? 'text-body' : 'text-muted'}>
              {selectedDisplay || placeholder}
            </span>
          )}
        </span>
        <BsChevronDown
          className="flex-shrink-0 text-secondary"
          size={18}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
          aria-hidden
        />
      </button>
      {open && (
        <div className="car-rental-location-search__dropdown border rounded bg-white shadow-sm mt-1 position-absolute w-100">
          <div className="p-2 border-bottom">
            <Form.Control
              ref={searchInputRef}
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="car-rental-location-search__search form-control-lg"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const flat = filtered.flatMap((g) => g.options);
                  const query = q.trim();
                  if (query) {
                    const exact = flat.find((opt) => normalizeSearchToken(opt.label) === normalizeSearchToken(query));
                    if (exact) {
                      pick(exact.value);
                      return;
                    }
                    if (flat.length === 1) {
                      pick(flat[0].value);
                      return;
                    }
                    if (flat.length === 0) {
                      pickCustom();
                    }
                    return;
                  }
                  if (flat.length === 1) {
                    pick(flat[0].value);
                  }
                }
                if (e.key === 'Escape') {
                  setOpen(false);
                  setQ('');
                }
              }}
            />
          </div>
          <div className="car-rental-location-search__list">
            {loading ? (
              <div className="px-3 py-2 text-muted small d-flex align-items-center gap-2 border-bottom">
                <Spinner animation="border" size="sm" />
                Loading locations...
              </div>
            ) : null}
            {q.trim() ? (
              <button
                type="button"
                className="w-100 text-start btn btn-link py-2 px-3 text-decoration-none border-bottom rounded-0"
                onClick={pickCustom}
              >
                Use “{q.trim()}”
              </button>
            ) : null}
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-muted small">
                {q.trim() ? 'No matches — use the option above or adjust your search.' : 'Start typing to search.'}
              </div>
            ) : (
              filtered.map((g) => (
                <div key={g.label}>
                  <div className="px-3 py-2 text-uppercase small fw-semibold text-muted bg-light border-bottom">
                    {g.label}
                  </div>
                  {g.options.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className="w-100 text-start btn btn-link py-2 px-3 text-decoration-none border-bottom rounded-0 text-dark"
                      onClick={() => pick(o.value)}
                    >
                      <div className="fw-semibold">{o.label}</div>
                      {o.secondary ? <div className="small text-muted">{o.secondary}</div> : null}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
